import classNames from 'classnames';
import React, { useMemo } from 'react';
import BaseModal from 'components/atoms/base-modal';
import BaseForm from 'components/atoms/base-form';
import BaseGrid from 'components/atoms/base-grid';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseDatepicker from 'components/atoms/base-datepicker';
import BaseTimePicker from 'components/atoms/base-timepicker';
import { DateFormats, Timezones } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { Flex, Typography } from 'antd';
import BaseButton from 'components/atoms/baseButton';
import { type INextAppointment } from 'infrastructure/interfaces';
import {
  DateFormatter,
  convertTimeZoneToUtc,
  convertDateToTimeZone,
  getUserTimeZone,
} from 'infrastructure/functions';
import { utcToZonedTime } from 'date-fns-tz';
import { parseDateSafe } from 'utils/timeHelper';

import styles from './styles.module.scss';
import { useUpdateAppointmentDueDate } from './hooks/use-update-appointment-due-date';

const { Text } = Typography;
interface IUpdateDueDateModalProps {
  nextAppointment?: INextAppointment;
  title?: string | React.ReactNode;
  open: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

const UpdateDueDateModal: React.FC<IUpdateDueDateModalProps> = ({
  nextAppointment,
  title,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const { formik, submitForm, updateLoading } = useUpdateAppointmentDueDate({
    patientGuid: nextAppointment?.patientGuid,
    nextAppointment,
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    },
  });

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
    formik.resetForm();
  };

  const formattedLocalDateTime = useMemo(() => {
    const { scheduledDate, scheduledTime } = formik.values.appointment || {};

    if (!scheduledDate || !scheduledTime) {
      return '';
    }

    try {
      const date = parseDateSafe(scheduledDate);
      if (!date) return '';

      const timeParts = scheduledTime.split(':');
      if (timeParts.length >= 2) {
        date.setHours(Number.parseInt(timeParts[0], 10));
        date.setMinutes(Number.parseInt(timeParts[1], 10));
        date.setSeconds(0);
      }

      const utcDate = convertTimeZoneToUtc(date, Timezones['America/Chicago']);
      const userTimeZone = getUserTimeZone();
      if (!userTimeZone) {
        return '';
      }
      const localDate = convertDateToTimeZone(utcDate, userTimeZone);

      return DateFormatter({
        date: localDate,
        format: DateFormats['MM/dd/yy - HH:mm:ss'],
      });
    } catch {
      return '';
    }
  }, [
    formik.values.appointment?.scheduledDate,
    formik.values.appointment?.scheduledTime,
  ]);

  if (!nextAppointment) return null;

  return (
    <BaseModal open={open} onCancel={handleCancel} title={title} centered>
      <BaseForm
        formik={formik}
        plaintext={false}
        readonly={false}
        onSave={submitForm}
        onCancel={handleCancel}
        loading={updateLoading}
      >
        <BaseGrid columns={2}>
          <BaseFormItem
            name="appointment.scheduledDate"
            label={t('homeDashboard.updateDueDateModal.dateLabel')}
            isBlackLabel
          >
            <BaseDatepicker disablePastDates />
          </BaseFormItem>
          <BaseFormItem
            name="appointment.scheduledTime"
            label={t('homeDashboard.updateDueDateModal.timeLabel')}
            isBlackLabel
          >
            <BaseTimePicker
              format={DateFormats['HH:mm']}
              disablePastTimes
              selectedDate={formik.values.appointment?.scheduledDate}
              timezone={Timezones['America/Chicago']}
              onNowClick={() => {
                formik.setFieldValue(
                  'appointment.scheduledDate',
                  utcToZonedTime(new Date(), Timezones['America/Chicago']),
                );
              }}
            />
          </BaseFormItem>
        </BaseGrid>
        {formattedLocalDateTime && (
          <Text className={styles['local-time-text']}>
            {t('homeDashboard.updateDueDateModal.yourLocalTime', {
              dateTime: formattedLocalDateTime,
            })}
          </Text>
        )}
        <Flex justify="end" className={styles['button-wrapper']}>
          <BaseButton
            type="secondary"
            className={classNames(styles.button, styles['cancel-button'])}
            label={t('homeDashboard.updateDueDateModal.cancelButton')}
            onClick={handleCancel}
          />
          <BaseButton
            className={classNames(styles.button, styles['save-button'])}
            label={t('homeDashboard.updateDueDateModal.saveButton')}
            onClick={submitForm}
            loading={updateLoading}
          />
        </Flex>
      </BaseForm>
    </BaseModal>
  );
};

export default UpdateDueDateModal;
